import { FC } from 'react';
import { Box, Card, CardContent, Typography, Grid, Chip, SxProps } from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import type { ComparisonResult } from '@/types/entities';

interface ComparisonSummaryProps {
  comparison: ComparisonResult;
  detailed?: boolean;
  sx?: SxProps;
}

export const ComparisonSummary: FC<ComparisonSummaryProps> = ({
  comparison,
  detailed: _detailed = false,
  sx,
}) => {
  const { summary, versionA, versionB } = comparison;

  const StatCard = ({ label, added, removed, modified }: {
    label: string;
    added: number;
    removed: number;
    modified: number;
  }) => (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          {added > 0 && (
            <Chip
              icon={<AddIcon />}
              label={`+${added}`}
              color="success"
              size="small"
            />
          )}
          {removed > 0 && (
            <Chip
              icon={<RemoveIcon />}
              label={`-${removed}`}
              color="error"
              size="small"
            />
          )}
          {modified > 0 && (
            <Chip
              icon={<EditIcon />}
              label={`~${modified}`}
              color="warning"
              size="small"
            />
          )}
          {added === 0 && removed === 0 && modified === 0 && (
            <Chip label="No changes" size="small" />
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Card sx={sx}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Comparison Summary
        </Typography>

        {/* Version Info */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Baseline (A)
            </Typography>
            <Typography variant="body2">
              {new Date(versionA.uploadTimestamp).toLocaleString()}
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary">
            →
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Comparison (B)
            </Typography>
            <Typography variant="body2">
              {new Date(versionB.uploadTimestamp).toLocaleString()}
            </Typography>
          </Box>
        </Box>

        {/* Statistics Grid */}
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <StatCard
              label="Interfaces"
              added={summary.interfacesAdded}
              removed={summary.interfacesRemoved}
              modified={summary.interfacesModified}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <StatCard
              label="VLANs"
              added={summary.vlansAdded}
              removed={summary.vlansRemoved}
              modified={summary.vlansModified}
            />
          </Grid>
        </Grid>

        {/* Total Changes */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Total Changes:</strong>{' '}
            {summary.interfacesAdded +
              summary.interfacesRemoved +
              summary.interfacesModified +
              summary.vlansAdded +
              summary.vlansRemoved +
              summary.vlansModified}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};
